import React from 'react';
import { AudienceCardProps } from '@/types/registration.types';
import HoverCard from '@ui/cards/hover-card/hover-card';
import HoverContent from '@ui/cards/hover-card/hover-content';
import PreHoverContent from '@ui/cards/hover-card/pre-hover-content';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import Link from '@ui/links/link';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { Activity } from 'react';
import Checkbox from '@ui/checkbox/checkbox';
import AudienceActions from './audience-actions';
import RestrictsAccessBadge from './restricts-access-badge';
import { getViewPath } from '../../utils';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import useLanguageParams from '@/utils/use-language-params';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import Button from '@ui/buttons/button/button';

interface AudienceCardComponentProps extends AudienceCardProps {
  isSelected: boolean;
  onCheckboxChange: (checked: boolean) => void;
  onCreateCampaign: () => void;
}

const AudienceCard = ({
  name,
  subHeader,
  audienceCardType,
  passcodeListId,
  passcodeList,
  member_count = '-',
  restrictsAccess,
  isSelected,
  onCheckboxChange,
  onCreateCampaign,
}: AudienceCardComponentProps) => {
  const projectUuid = useProjectUuidParam();
  const language = useLanguageParams();

  // clicking anywhere on the card that is not a dedicated option/action should select the card.
  // stopPropagation to ensure the select happens
  const stopProp = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <HoverCard delay={100} height="162px" width="319px" backgroundColor="var(--global-cont-1)">
      <PreHoverContent>
        <Container
          height="100%"
          width="100%"
          className="padding-h-20 padding-t-20 padding-b-16 flex flex-column jc-space-between"
          borderRadius="var(--card-radius)"
          border={`1px solid var(${isSelected ? '--global-ico-text-1' : '--global-cont-1'})`}
          background="var(--global-cont-1)"
          style={{ cursor: 'pointer' }}
        >
          <Container className="flex fd-column gap-8">
            <Container className="flex fd-row jc-space-between ai-center">
              <Text className="H5">{name}</Text>
              <Activity mode={isSelected ? 'visible' : 'hidden'}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onCheckboxChange(!isSelected)}
                  value={passcodeListId || -1}
                  selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                  unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                  color="var(--global-ico-text-1)"
                />
              </Activity>
            </Container>
            <Text className="P2">{subHeader}</Text>
          </Container>

          <Container className="flex fd-row jc-flex-end ai-center gap-8">
            {restrictsAccess && <RestrictsAccessBadge variant="chip" />}
            <Container
              className="flex jc-flex-end radius-8 padding-v-8 padding-h-10 ai-center gap-6"
              background="var(--global-cont-2)"
            >
              <Icon name={ICONS.USERS_AUDIENCE} size={12} color="var(--global-ico-text-2)" />
              <Text className="C1" design="GlobalIcoText2">
                {member_count}
              </Text>
            </Container>
          </Container>
        </Container>
      </PreHoverContent>

      <HoverContent>
        <Container
          height="100%"
          width="100%"
          className="padding-h-20 padding-t-20 padding-b-16 flex flex-column jc-space-between"
          borderRadius="var(--card-radius)"
          border={`1px solid var(${isSelected ? '--global-ico-text-1' : '--global-cont-2'})`}
          background="var(--global-cont-2)"
          style={{ cursor: 'pointer' }}
          onClick={() => onCheckboxChange(!isSelected)}
        >
          <Container className="flex fd-column gap-8">
            <Container className="flex fd-row jc-space-between ai-center">
              <Container className="flex fd-row ai-center gap-6">
                <Text className="H5">{name}</Text>
                {restrictsAccess && <RestrictsAccessBadge variant="icon" />}
              </Container>
              <span onClick={stopProp}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onCheckboxChange(!isSelected)}
                  value={passcodeListId || -1}
                  selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                  unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                />
              </span>
            </Container>
            <Text className="P2">{subHeader}</Text>
          </Container>

          <Container className="flex fd-row jc-flex-start gap-8" onClick={stopProp}>
            <Link
              to={getViewPath({ audienceCardType, passcodeListId, projectUuid, language })}
              className="flex fd-row ai-center gap-6 padding-v-6 padding-h-16"
              borderRadius="100px"
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              height="32px"
              textColor="none"
            >
              <Text className="L2" color={getThemeColorContrastCSSVariable()}>
                View
              </Text>
            </Link>

            <Button
              className="flex gap-6 ai-center"
              design={TButtonAndLinkDesigns.LocalBttnSec}
              height="32px"
              onClick={onCreateCampaign}
            >
              <Icon name={ICONS.AI_SEND_MESSAGE} size={12} color="var(--global-ico-text-2)" />
              <Text className="L2" design="GlobalIcoText2">
                Create campaign
              </Text>
            </Button>

            <AudienceActions
              audienceCardType={audienceCardType}
              passcodeListId={passcodeListId}
              passcodeList={passcodeList}
              name={name}
            />
          </Container>
        </Container>
      </HoverContent>
    </HoverCard>
  );
};

export default AudienceCard;
